import React, { useEffect, useState } from "react";
import {
  TeamOutlined,
  HomeOutlined,
  SettingOutlined,
  ScheduleOutlined,
  CoffeeOutlined,
  FieldTimeOutlined,
  HeartOutlined,
  CreditCardOutlined,
  ShopOutlined,
  CarryOutOutlined,
  SwapOutlined,
  LineChartOutlined,
  DollarOutlined,
  ApartmentOutlined,
  ExclamationCircleOutlined,
  FileProtectOutlined,
  BankOutlined,
  WalletOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  FolderOutlined,
  RadarChartOutlined,
  CloseOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  AuditOutlined,
  ShoppingOutlined,
  MoneyCollectOutlined,
  FileTextOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { Popover, List, Spin, Tooltip } from "antd";
import LogoIcon from "../../assets/fixmanLogo.svg";
import FIcon from "../../assets/images/F.png";

import axios from "axios";
import { useTranslation } from "react-i18next";
import SelectLanguage from "./Components/SelectLanguage/SelectLanguage";
import UserMenu from "./Components/UserMenu/UserMenu";
import VoiceAssistantButton from "../VoiceAssistant/VoiceAssistantButton";
import { AuthProvider } from "../../utils/useAuth";
import { BASE_API_URL } from "../../utils/consts";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../utils/redux/slices/userDataSlice";
import "./Layout.scss";
import { UserRoleTypes } from "./types";
import { isAdmin, isModerator, isSuperAdmin } from "../../utils/permissions";
import { usePermissions } from "../../utils/usePermissions";

import { ReactComponent as NotificationsIcon } from "../../assets/images/icons/notifications.svg";
import { subscriptionAPI } from "../../utils/api/subscriptionAPI";

type Props = {
  children?: any;
};

interface ChildNavItemData {
  key: string;
  label: string;
  accessRoles: UserRoleTypes[];
  featureKey?: string;
  disabled?: boolean;
}

interface NavItemData {
  key: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  featureKey?: string;
  accessRoles: UserRoleTypes[];
  children?: ChildNavItemData[];
  disabled?: boolean;
}

const LayoutContainer: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<any>(undefined);
  const [openSubMenuKeys, setOpenSubMenuKeys] = useState<Set<string>>(new Set());
  const userData = useSelector((state: any) => state.userData.user);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [openNotification, setOpenNotification] = useState(false);
  const [unpaidCount, setUnpaidCount] = useState<number>(0);
  const [unpaidCreditsCount, setUnpaidCreditsCount] = useState<number>(0);
  const isLoaderActive = useSelector(
    (state: any) => state.loader.isLoaderActive,
  );
  const isSuperUser = isSuperAdmin(userData?.roles);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | undefined>(
    userData?.clientId?.settings?.enabledFeatures,
  );
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [expiringCount, setExpiringCount] = useState<number>(0);
  const [expiringContractsCount, setExpiringContractsCount] =
    useState<number>(0);
  const loggedInUser: any = localStorage.getItem("user");
  const getLanguage = localStorage.getItem("userLanguage");
  const user = localStorage.getItem("user") ?? undefined;
  const parsedUser = React.useMemo(() => {
    if (!user) return undefined;
    try {
      return JSON.parse(user);
    } catch (err) {
      console.error("Failed to parse user from localStorage", err);
      return undefined;
    }
  }, [user]);
  const clientId = userData?.clientId || parsedUser?.clientId;

  useEffect(() => {
    i18n.changeLanguage(getLanguage?.toString());
  }, [getLanguage]);

  // ─── Nav items ────────────────────────────────────────────────────────────
  const navItemsData: NavItemData[] = [
    {
      key: "0",
      icon: <HomeOutlined />,
      label: t("Dashboard"),
      featureKey: "dashboard",
      accessRoles: [UserRoleTypes.USER],
    },
    // ── Operacije ──
    {
      key: "26",
      icon: <RadarChartOutlined />,
      label: t("On Duty"),
      featureKey: "shifts",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "23",
      icon: <CalendarOutlined />,
      label: t("Reservations"),
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "8",
      icon: <SwapOutlined />,
      label: t("Shifts"),
      featureKey: "shifts",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "15",
      icon: <DollarOutlined />,
      label: t("Daily Revenue"),
      featureKey: "dailyRevenue",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "1",
      icon: <CarryOutOutlined />,
      label: t("Appointments"),
      featureKey: "appointments",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "25",
      icon: <HomeOutlined />,
      label: t("Apartments"),
      featureKey: "apartments",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "4",
      icon: <ScheduleOutlined />,
      label: t("Schedules"),
      featureKey: "schedules",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "5",
      icon: <CoffeeOutlined />,
      label: t("Leaves"),
      featureKey: "leaves",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "9",
      icon: <FieldTimeOutlined />,
      label: t("Overtimes"),
      featureKey: "overtime",
      accessRoles: [UserRoleTypes.USER],
    },
    // ── Finansije ──
    {
      key: "22",
      icon: <WalletOutlined />,
      label: t("Financial Overview"),
      featureKey: "business",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "30",
      icon: <FileTextOutlined />,
      label: "Fakture",
      accessRoles: [UserRoleTypes.USER],
      children: [
        { key: "30-1", label: "eFakture", accessRoles: [UserRoleTypes.USER] },
        { key: "30-2", label: t("Own Invoices"), accessRoles: [UserRoleTypes.USER] },
      ],
    },
    {
      key: "33",
      icon: <FileProtectOutlined />,
      label: "Izvodi",
      accessRoles: [UserRoleTypes.USER],
      disabled: true,
    },
    {
      key: "34",
      icon: <LineChartOutlined />,
      label: "Analitika",
      accessRoles: [UserRoleTypes.USER],
      disabled: true,
    },
    {
      key: "21",
      icon: <BankOutlined />,
      label: t("Credits"),
      badge: unpaidCreditsCount > 0 ? unpaidCreditsCount : undefined,
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "19",
      icon: <DollarOutlined />,
      label: t("Client Investments"),
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "13",
      icon: <ShopOutlined />,
      label: t("Business"),
      featureKey: "business",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "16",
      icon: <DollarOutlined />,
      label: t("Investments"),
      featureKey: "investments",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "6",
      icon: <CreditCardOutlined />,
      label: t("Transactions"),
      featureKey: "payments",
      accessRoles: [UserRoleTypes.USER],
    },
    // ── Administracija ──
    {
      key: "32",
      icon: <TeamOutlined />,
      label: "Zaposleni",
      accessRoles: [UserRoleTypes.USER],
      children: [
        {
          key: "2",
          label: "Pregled zaposlenih",
          featureKey: "staff",
          accessRoles: [UserRoleTypes.USER],
        },
        {
          key: "29",
          label: "Plate",
          featureKey: "business",
          accessRoles: [UserRoleTypes.USER],
        },
        {
          key: "32-1",
          label: "Bonus šema",
          accessRoles: [UserRoleTypes.USER],
          disabled: true,
        },
        {
          key: "32-2",
          label: "Edukacija osoblja",
          accessRoles: [UserRoleTypes.USER],
          disabled: true,
        },
      ],
    },
    {
      key: "28",
      icon: <ShoppingOutlined />,
      label: t("Dobavljači"),
      featureKey: "suppliers",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "24",
      icon: <FolderOutlined />,
      label: t("Documents"),
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "20",
      icon: <ApartmentOutlined />,
      label: t("Organization Chart"),
      featureKey: "hierarchy",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "3",
      icon: <HeartOutlined />,
      label: t("Patients"),
      featureKey: "patients",
      accessRoles: [UserRoleTypes.USER],
    },
    // ── POS (Octopos) ──
    {
      key: "31-1",
      icon: <FileTextOutlined />,
      label: "Računi",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "31-2",
      icon: <AppstoreOutlined />,
      label: "Proizvodi",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "31-3",
      icon: <SnippetsOutlined />,
      label: "Kategorije proizvoda",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "31-4",
      icon: <SwapOutlined />,
      label: "Smene",
      accessRoles: [UserRoleTypes.USER],
    },
    // ── System ──
    {
      key: "10",
      icon: <SettingOutlined />,
      label: t("Company Settings"),
      featureKey: "settings",
      accessRoles: [UserRoleTypes.USER],
    },
    {
      key: "27",
      icon: <AuditOutlined />,
      label: t("Activity Trail"),
      accessRoles: [UserRoleTypes.ADMIN],
    },
  ];

  const navGroupConfig = [
    { groupKey: "overview", label: t("Overview"), itemKeys: ["0"] },
    {
      groupKey: "operacije",
      label: "Operacije",
      itemKeys: ["26", "23", "8", "15", "1", "25", "4", "5", "9"],
    },
    {
      groupKey: "finansije",
      label: t("Finance"),
      itemKeys: ["22", "30", "33", "34", "21", "19", "13", "16", "6"],
    },
    {
      groupKey: "administracija",
      label: "Administracija",
      itemKeys: ["32", "28", "24", "20", "3", "7"],
    },
    {
      groupKey: "pos",
      label: "POS (Octopos)",
      itemKeys: ["31-1", "31-2", "31-3", "31-4"],
    },
    { groupKey: "system", label: t("System"), itemKeys: ["10", "27"] },
  ];

  // ─── Route map ────────────────────────────────────────────────────────────
  const routeMap: Record<string, string> = {
    "0": "/",
    "1": "/appointments",
    "2": "/users",
    "3": "/patients",
    "4": "/schedules",
    "5": "/leaves",
    "6": "/payments",
    "8": "/shifts",
    "9": "/overtimes",
    "13": "/business",
    "15": "/dailyRevenue",
    "16": "/investments",
    "17": "/admin/subscriptions",
    "18": "/admin/plans",
    "19": "/investments-v2",
    "20": "/hierarchy",
    "21": "/credits",
    "22": "/finance",
    "23": "/reservations",
    "25": "/apartment-reservations",
    "26": "/on-duty",
    "10": "settings?tab=general",
    "14": "/support",
    "24": "/documents",
    "27": "/activity-trail",
    "28": "/suppliers",
    "29": "/salaries",
    "30-1": "/invoices/efakture",
    "30-2": "/invoices/own",
    "31-1": "/octopus/bills",
    "31-2": "/octopus/products",
    "31-3": "/octopus/categories",
    "31-4": "/octopus/shifts",
  };

  // ─── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!loggedInUser) {
      navigation("login");
    }
    dispatch(setUserData(loggedInUser ? JSON.parse(loggedInUser) : {}));
  }, [loggedInUser]);

  useEffect(() => {
    if (!clientId) {
      if (!isSuperAdmin(userData?.roles)) {
        setEnabledFeatures(userData?.clientId?.settings?.enabledFeatures || []);
      }
      return;
    }
    setFeaturesLoading(true);
    axios
      .get(`${BASE_API_URL}/client/${clientId}/features`)
      .then((res) => {
        setEnabledFeatures(
          res.data?.enabledFeatures ||
            userData?.clientId?.settings?.enabledFeatures ||
            [],
        );
      })
      .catch((err) => {
        console.error(err);
        setEnabledFeatures(userData?.clientId?.settings?.enabledFeatures || []);
      })
      .finally(() => setFeaturesLoading(false));
  }, [
    clientId,
    userData?.roles,
    userData?.clientId?.settings?.enabledFeatures,
  ]);

  const fetchNotifications = () => {
    axios
      .get(`${BASE_API_URL}/notifications/${userData?.id}`)
      .then((res) => setNotifications(res.data))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (openNotification && userData?.id) {
      fetchNotifications();
    }
  }, [openNotification, userData]);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      const effectiveClientId = userData?.clientId || parsedUser?.clientId;
      if (!effectiveClientId) return;
      try {
        const response = await subscriptionAPI.getQuickStatus();
        if (response.data?.data) {
          setUnpaidCount(response.data.data.unpaidCount || 0);
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      }
    };
    fetchSubscriptionStatus();
  }, [userData, parsedUser]);

  useEffect(() => {
    const rawClientId = userData?.clientId || parsedUser?.clientId;
    const effectiveClientId =
      typeof rawClientId === "object" ? rawClientId?._id : rawClientId;
    if (!effectiveClientId) return;
    axios
      .get(`${BASE_API_URL}/${effectiveClientId}/company-info`)
      .then((res) =>
        setExpiringCount(res.data?.upcomingExpirations?.length || 0),
      )
      .catch((err) => console.error("company-info error", err));
  }, [userData, parsedUser]);

  useEffect(() => {
    const rawClientId = userData?.clientId || parsedUser?.clientId;
    const effectiveClientId =
      typeof rawClientId === "object" ? rawClientId?._id : rawClientId;
    if (!effectiveClientId) return;
    axios
      .get(`${BASE_API_URL}/${effectiveClientId}/company-contracts`)
      .then((res) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysLater = new Date();
        thirtyDaysLater.setDate(today.getDate() + 30);
        thirtyDaysLater.setHours(23, 59, 59, 999);
        // Only count contracts expiring within the next 30 days (not already expired)
        const count = (res.data ?? []).filter((c: any) => {
          const end = new Date(c.endDate);
          return end >= today && end <= thirtyDaysLater;
        }).length;
        setExpiringContractsCount(count);
      })
      .catch(() => {});
  }, [userData, parsedUser]);

  useEffect(() => {
    const fetchUnpaidCreditsCount = async () => {
      const rawClientId = userData?.clientId || parsedUser?.clientId;
      const effectiveClientId =
        typeof rawClientId === "object" ? rawClientId?._id : rawClientId;
      if (!effectiveClientId) return;
      try {
        const token = userData?.accessToken || parsedUser?.accessToken;
        const response = await axios.get(
          `${BASE_API_URL}/credits-stats/unpaid-count`,
          {
            params: { clientId: effectiveClientId },
            headers: token ? { "x-access-token": token } : {},
          },
        );
        setUnpaidCreditsCount(response.data.unpaidCount || 0);
      } catch (error) {
        console.error("Failed to fetch unpaid credits count:", error);
      }
    };
    fetchUnpaidCreditsCount();
    const handleCreditsUpdated = () => fetchUnpaidCreditsCount();
    window.addEventListener("credits-updated", handleCreditsUpdated);
    return () =>
      window.removeEventListener("credits-updated", handleCreditsUpdated);
  }, [userData, parsedUser]);

  // ─── Active route sync ─────────────────────────────────────────────────────
  useEffect(() => {
    const path = location?.pathname;
    if (path === "/") setActiveMenu("0");
    else if (path.includes("appointments")) setActiveMenu("1");
    else if (path.includes("users")) {
      setActiveMenu("2");
      setOpenSubMenuKeys((prev) => new Set(prev).add("32"));
    } else if (path.includes("hierarchy")) setActiveMenu("20");
    else if (path.includes("patient")) setActiveMenu("3");
    else if (path.includes("shifts")) setActiveMenu("8");
    else if (path.includes("schedules")) setActiveMenu("4");
    else if (path.includes("leaves")) setActiveMenu("5");
    else if (path.includes("payments")) setActiveMenu("6");
    else if (path.includes("overtimes")) setActiveMenu("9");
    else if (path.includes("apartment-reservations")) setActiveMenu("25");
    else if (path.includes("reservations") && !path.includes("public"))
      setActiveMenu("23");
    else if (path.includes("on-duty")) setActiveMenu("26");
    else if (path.includes("finance")) setActiveMenu("22");
    else if (path.includes("business")) setActiveMenu("13");
    else if (path.includes("dailyRevenue")) setActiveMenu("15");
    else if (path.includes("investments-v2")) setActiveMenu("19");
    else if (path.includes("credits")) setActiveMenu("21");
    else if (path.includes("investments")) setActiveMenu("16");
    else if (path.includes("subscriptions")) setActiveMenu("17");
    else if (path.includes("documents")) setActiveMenu("24");
    else if (path.includes("activity-trail")) setActiveMenu("27");
    else if (path.includes("suppliers")) setActiveMenu("28");
    else if (path.includes("salaries")) {
      setActiveMenu("29");
      setOpenSubMenuKeys((prev) => new Set(prev).add("32"));
    } else if (path.includes("invoices/efakture")) {
      setActiveMenu("30-1");
      setOpenSubMenuKeys((prev) => new Set(prev).add("30"));
    } else if (path.includes("invoices/own")) {
      setActiveMenu("30-2");
      setOpenSubMenuKeys((prev) => new Set(prev).add("30"));
    } else if (path.includes("octopus/bills")) {
      setActiveMenu("31-1");
    } else if (path.includes("octopus/products")) {
      setActiveMenu("31-2");
    } else if (path.includes("octopus/categories")) {
      setActiveMenu("31-3");
    } else if (path.includes("octopus/shifts")) {
      setActiveMenu("31-4");
    }
  }, [location.pathname]);

  // ─── Permissions ───────────────────────────────────────────────────────────
  const hasFeatureAccess = (featureKey?: string) => {
    if (!featureKey) return true;
    if (isSuperAdmin(userData?.roles)) return true;
    const featuresSource =
      enabledFeatures ?? userData?.clientId?.settings?.enabledFeatures;
    if (!featuresSource) return false;
    return featuresSource.includes(featureKey);
  };

  const permissionByMenuKey: Record<string, string> = {
    "0": "dashboard:view",
    "13": "business:view-summary",
    "15": "settings-daily-revenue:view",
    "16": "investments:view",
    "19": "client-investments:view",
    "21": "credits:view",
    "22": "expenses:view",
    "1": "appointments:view",
    "4": "schedules:view",
    "5": "leaves:view",
    "9": "overtimes:view",
    "2": "staff:view",
    "20": "hierarchy:view",
    "8": "shifts:view",
    "3": "patients:view",
    "6": "payments:view",
    "10": "settings-general:update-company",
    "14": "support:view",
    "23": "reservations:view",
    "25": "apartments:view",
    "24": "documents:view",
    "27": "activity-trail:view",
    "28": "suppliers:view",
    "29": "salaries:view",
    "30-1": "invoices:view",
    "30-2": "own-invoices:view",
    "31-1": "octopus-bills:view",
    "31-2": "octopus-products:view",
    "31-3": "octopus-categories:view",
    "31-4": "octopus-shifts:view",
    "26": "shifts:view",
    "32-1": "staff:view",
    "32-2": "staff:view",
    "33": "expenses:view",
    "34": "expenses:view",
  };

  const hasMenuPermission = (permissionKey?: string) => {
    if (!permissionKey) return true;
    if (hasPermission(permissionKey)) return true;
    if ((userData?.permissions || []).includes(permissionKey)) return true;
    return false;
  };

  const canShowChildItem = (child: ChildNavItemData): boolean => {
    if (isSuperAdmin(userData?.roles)) return true;
    if (!hasFeatureAccess(child.featureKey)) return false;
    const permissionKey = permissionByMenuKey[child.key];
    if (permissionKey && !hasMenuPermission(permissionKey)) return false;
    if (!child.accessRoles?.length) return true;
    return child.accessRoles.some((role: UserRoleTypes) => {
      if (role === UserRoleTypes.ADMIN) return isAdmin(userData?.roles);
      if (role === UserRoleTypes.MODERATOR) return isModerator(userData?.roles);
      if (role === UserRoleTypes.USER) return true;
      return false;
    });
  };

  const canShowMenuItem = (item: NavItemData) => {
    if (isSuperAdmin(userData?.roles)) return true;
    if (item.children?.length) {
      return item.children.some(canShowChildItem);
    }
    if (!hasFeatureAccess(item.featureKey)) return false;
    const permissionKey = permissionByMenuKey[item.key as string];
    if (permissionKey && !hasMenuPermission(permissionKey)) return false;
    if (!item.accessRoles?.length) return true;
    return (
      isSuperAdmin(userData?.roles) ||
      item.accessRoles.some((role: UserRoleTypes) => {
        if (role === UserRoleTypes.ADMIN) return isAdmin(userData?.roles);
        if (role === UserRoleTypes.MODERATOR)
          return isModerator(userData?.roles);
        if (role === UserRoleTypes.USER) return true;
        return false;
      })
    );
  };

  // ─── Navigation click ─────────────────────────────────────────────────────
  const handleNavClick = (key: string) => {
    setActiveMenu(key);
    const route = routeMap[key];
    if (route) navigation(route);
  };

  const toggleSubMenu = (key: string) => {
    setOpenSubMenuKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ─── Breadcrumbs ──────────────────────────────────────────────────────────
  const getBreadcrumbs = (): { section: string; page: string } => {
    const path = location.pathname;
    if (path === "/") return { section: t("Overview"), page: t("Dashboard") };
    if (path.includes("appointments"))
      return { section: "Operacije", page: t("Appointments") };
    if (path.includes("reservations") && !path.includes("public"))
      return { section: "Operacije", page: t("Reservations") };
    if (path.includes("schedules"))
      return { section: "Operacije", page: t("Schedules") };
    if (path.includes("leaves"))
      return { section: "Operacije", page: t("Leaves") };
    if (path.includes("overtimes"))
      return { section: "Operacije", page: t("Overtimes") };
    if (path.includes("shifts"))
      return { section: "Operacije", page: t("Shifts") };
    if (path.includes("investments-v2"))
      return { section: t("Finance"), page: t("Client Investments") };
    if (path.includes("investments"))
      return { section: t("Finance"), page: t("Investments") };
    if (path.includes("business"))
      return { section: t("Finance"), page: t("Business") };
    if (path.includes("finance"))
      return { section: t("Finance"), page: t("Financial Overview") };
    if (path.includes("apartment-reservations"))
      return { section: t("Apartments"), page: t("Apartments") };
    if (path.includes("reservations"))
      return { section: t("Reservations"), page: t("Reservations") };
    if (path.includes("dailyRevenue"))
      return { section: "Operacije", page: t("Daily Revenue") };
    if (path.includes("credits"))
      return { section: t("Finance"), page: t("Credits") };
    if (path.includes("payments"))
      return { section: t("Finance"), page: t("Transactions") };
    if (path.includes("salaries"))
      return { section: "Administracija", page: t("Salaries") };
    if (path.includes("invoices/efakture"))
      return { section: t("Invoices"), page: "eFakture" };
    if (path.includes("invoices/own"))
      return { section: t("Invoices"), page: t("Own Invoices") };
    if (path.includes("octopus/bills"))
      return { section: "POS (Octopos)", page: "Računi" };
    if (path.includes("octopus/products"))
      return { section: "POS (Octopos)", page: "Proizvodi" };
    if (path.includes("octopus/categories"))
      return { section: "POS (Octopos)", page: "Kategorije proizvoda" };
    if (path.includes("octopus/shifts"))
      return { section: "POS (Octopos)", page: "Smene" };
    if (path.includes("suppliers/"))
      return { section: "Administracija", page: "Profil dobavljača" };
    if (path.includes("suppliers"))
      return { section: "Administracija", page: "Dobavljači" };
    if (path.includes("users/"))
      return { section: "Administracija", page: t("Member Profile") };
    if (path.includes("users"))
      return { section: "Administracija", page: t("Members") };
    if (path.includes("hierarchy"))
      return { section: "Administracija", page: t("Organization Chart") };
    if (path.includes("patient"))
      return { section: "Administracija", page: t("Patients") };
    if (path.includes("settings"))
      return { section: t("System"), page: t("Company Settings") };
    if (path.includes("documents"))
      return { section: "Administracija", page: t("Documents") };
    if (path.includes("support"))
      return { section: t("System"), page: t("Support") };
    if (path.includes("subscription"))
      return { section: t("System"), page: t("Subscription") };
    return { section: "FixmanHR", page: "" };
  };

  // ─── Mobile items ─────────────────────────────────────────────────────────
  const allMobileItems = [
    {
      key: "dashboard",
      icon: <HomeOutlined />,
      label: t("Dashboard"),
      onClick: () => {
        setActiveMenu("0");
        navigation("/");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (isAdmin(userData?.roles) &&
          hasFeatureAccess("dashboard") &&
          hasPermission("dashboard:view")),
      isActive: location.pathname === "/",
    },
    {
      key: "business",
      icon: <ShopOutlined />,
      label: t("Business"),
      onClick: () => {
        setActiveMenu("13");
        navigation("/business");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("business") &&
          hasPermission("business:view-summary")),
      isActive: location.pathname.includes("business"),
    },
    {
      key: "finance",
      icon: <WalletOutlined />,
      label: t("Finance"),
      onClick: () => {
        setActiveMenu("22");
        navigation("/finance");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("business") && hasPermission("expenses:view")),
      isActive: location.pathname.includes("finance"),
    },
    {
      key: "dailyRevenue",
      icon: <DollarOutlined />,
      label: t("Daily Revenue"),
      onClick: () => {
        setActiveMenu("15");
        navigation("/dailyRevenue");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("dailyRevenue") &&
          hasPermission("settings-daily-revenue:view")),
      isActive: location.pathname.includes("dailyRevenue"),
    },
    {
      key: "investments",
      icon: <DollarOutlined />,
      label: t("Investments"),
      onClick: () => {
        setActiveMenu("16");
        navigation("/investments");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("investments") && hasPermission("investments:view")),
      isActive:
        location.pathname.includes("investments") &&
        !location.pathname.includes("investments-v2"),
    },
    {
      key: "client-investments",
      icon: <DollarOutlined />,
      label: t("Client Investments"),
      onClick: () => {
        setActiveMenu("19");
        navigation("/investments-v2");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (isAdmin(userData?.roles) && hasPermission("client-investments:view")),
      isActive: location.pathname.includes("investments-v2"),
    },
    {
      key: "credits",
      icon: <BankOutlined />,
      label: t("Credits"),
      badge: unpaidCreditsCount > 0 ? unpaidCreditsCount : undefined,
      onClick: () => {
        setActiveMenu("21");
        navigation("/credits");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasPermission("credits:view") && isAdmin(userData?.roles)),
      isActive: location.pathname.includes("credits"),
    },
    {
      key: "patients",
      icon: <HeartOutlined />,
      label: t("Patients"),
      onClick: () => {
        setActiveMenu("3");
        navigation("/patients");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("patients") && hasPermission("patients:view")),
      isActive: location.pathname.includes("patient"),
    },
    {
      key: "appointments",
      icon: <CarryOutOutlined />,
      label: t("Appointments"),
      onClick: () => {
        setActiveMenu("1");
        navigation("/appointments");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("appointments") &&
          hasPermission("appointments:view")),
      isActive: location.pathname.includes("appointments"),
    },
    {
      key: "reservations",
      icon: <CalendarOutlined />,
      label: t("Reservations"),
      onClick: () => {
        setActiveMenu("23");
        navigation("/reservations");
        setMobileDrawerOpen(false);
      },
      visible: isSuperUser || hasPermission("reservations:view"),
      isActive:
        location.pathname.includes("reservations") &&
        !location.pathname.includes("apartment-reservations"),
    },
    {
      key: "apartment-reservations",
      icon: <HomeOutlined />,
      label: t("Apartments"),
      onClick: () => {
        setActiveMenu("25");
        navigation("/apartment-reservations");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("apartments") && hasPermission("apartments:view")),
      isActive: location.pathname.includes("apartment-reservations"),
    },
    {
      key: "leaves",
      icon: <CoffeeOutlined />,
      label: t("Leaves"),
      onClick: () => {
        setActiveMenu("5");
        navigation("/leaves");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        ((isAdmin(userData?.roles) || isModerator(userData?.roles)) &&
          hasFeatureAccess("leaves") &&
          hasPermission("leaves:view")),
      isActive: location.pathname.includes("leaves"),
    },
    {
      key: "shifts",
      icon: <SwapOutlined />,
      label: t("Shifts"),
      onClick: () => {
        setActiveMenu("8");
        navigation("/shifts");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("shifts") && hasPermission("shifts:view")),
      isActive: location.pathname.includes("shifts"),
    },
    {
      key: "members",
      icon: <TeamOutlined />,
      label: t("Members"),
      onClick: () => {
        setActiveMenu("2");
        navigation("/users");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("staff") && hasPermission("staff:view")),
      isActive: location.pathname.includes("users"),
    },
    {
      key: "hierarchy",
      icon: <ApartmentOutlined />,
      label: t("Organization Chart"),
      onClick: () => {
        setActiveMenu("20");
        navigation("/hierarchy");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("hierarchy") && hasPermission("hierarchy:view")),
      isActive: location.pathname.includes("hierarchy"),
    },
    {
      key: "schedules",
      icon: <ScheduleOutlined />,
      label: t("Schedules"),
      onClick: () => {
        setActiveMenu("4");
        navigation("/schedules");
        setMobileDrawerOpen(false);
      },
      visible:
        isSuperUser ||
        (hasFeatureAccess("schedules") && hasPermission("schedules:view")),
      isActive: location.pathname.includes("schedules"),
    },
    {
      key: "suppliers",
      icon: <ShoppingOutlined />,
      label: "Dobavljači",
      onClick: () => {
        setActiveMenu("28");
        navigation("/suppliers");
        setMobileDrawerOpen(false);
      },
      visible: isSuperUser || hasPermission("suppliers:view"),
      isActive: location.pathname.includes("suppliers"),
    },
    {
      key: "salaries",
      icon: <MoneyCollectOutlined />,
      label: t("Salaries"),
      onClick: () => {
        setActiveMenu("29");
        navigation("/salaries");
        setMobileDrawerOpen(false);
      },
      visible: true,
      isActive: location.pathname.includes("salaries"),
    },
    {
      key: "invoices",
      icon: <FileTextOutlined />,
      label: t("Invoices"),
      onClick: () => {
        setActiveMenu("30");
        navigation("/invoices");
        setMobileDrawerOpen(false);
      },
      visible: true,
      isActive: location.pathname.includes("invoices"),
    },
    {
      key: "support",
      icon: <SettingOutlined />,
      label: t("Support"),
      onClick: () => {
        setActiveMenu("14");
        navigation("/support");
        setMobileDrawerOpen(false);
      },
      visible: isSuperUser || hasPermission("support:view"),
      isActive: location.pathname.includes("support"),
    },
  ].filter((item) => item.visible);

  const TAB_COUNT = 4;
  const tabItems = allMobileItems.slice(0, TAB_COUNT);
  const hasActiveInDrawer = allMobileItems
    .slice(TAB_COUNT)
    .some((i) => i.isActive);

  // ─── User initials ─────────────────────────────────────────────────────────
  const userInitials =
    [userData?.firstName?.charAt(0), userData?.lastName?.charAt(0)]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <AuthProvider user={user ? JSON.parse(user) : ""}>
      {/* ── Mobile Navigation ── */}
      <div className="mobileNavigation">
        <div className="mobileTabBar">
          {tabItems.map((item) => (
            <button
              key={item.key}
              className={`mobileTabItem${item.isActive ? " mobileTabItem--active" : ""}`}
              onClick={item.onClick}
            >
              <span className="mobileTabItem__icon">
                {item.icon}
                {item.badge && (
                  <span className="mobileTabItem__badge">{item.badge}</span>
                )}
              </span>
              <span className="mobileTabItem__label">{item.label}</span>
            </button>
          ))}
          {allMobileItems.length > TAB_COUNT && (
            <button
              className={`mobileTabItem${mobileDrawerOpen || hasActiveInDrawer ? " mobileTabItem--active" : ""}`}
              onClick={() => setMobileDrawerOpen((v) => !v)}
            >
              <span className="mobileTabItem__icon">
                {mobileDrawerOpen ? <CloseOutlined /> : <AppstoreOutlined />}
              </span>
              <span className="mobileTabItem__label">{t("More")}</span>
            </button>
          )}
        </div>
      </div>

      {mobileDrawerOpen && (
        <div
          className="mobileDrawerOverlay"
          onClick={() => setMobileDrawerOpen(false)}
        />
      )}

      <div
        className={`mobileDrawer${mobileDrawerOpen ? " mobileDrawer--open" : ""}`}
      >
        <div className="mobileDrawer__handle" />
        <div className="mobileDrawer__title">{t("Navigation")}</div>
        <div className="mobileDrawer__grid">
          {allMobileItems.map((item) => (
            <button
              key={item.key}
              className={`mobileDrawerItem${item.isActive ? " mobileDrawerItem--active" : ""}`}
              onClick={item.onClick}
            >
              <span className="mobileDrawerItem__icon">
                {item.icon}
                {item.badge && (
                  <span className="mobileDrawerItem__badge">{item.badge}</span>
                )}
              </span>
              <span className="mobileDrawerItem__label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── App Shell ── */}
      <div className="appShell">
        {/* ── Sidebar ── */}
        <aside
          className={`appSidebar${collapsed ? " appSidebar--collapsed" : ""}`}
          aria-label="Main navigation"
        >
          {/* Brand */}
          <div className="sidebarBrand">
            <div className="sidebarBrand__mark">
              <img src={FIcon} alt="F" className="sidebarBrand__markImg" />
            </div>
            {!collapsed && (
              <div className="sidebarBrand__text">
                <span className="sidebarBrand__appName">FixmanHR</span>
                <span className="sidebarBrand__workspace">
                  {userData?.client?.name ?? "Demo workspace"}
                </span>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="sidebarSearch">
            <SearchOutlined className="sidebarSearch__icon" />
            <span className="sidebarSearch__placeholder">{t("Search")}…</span>
            <kbd className="sidebarSearch__kbd">⌘K</kbd>
          </div>

          {/* Nav */}
          <nav className="sidebarNav">
            <Spin
              spinning={
                (featuresLoading || permissionsLoading) &&
                !isSuperAdmin(userData?.roles)
              }
              size="small"
            >
              {navGroupConfig.map((group) => {
                const groupItems = group.itemKeys
                  .map((key) => navItemsData.find((item) => item.key === key))
                  .filter(
                    (item): item is NavItemData =>
                      !!item && canShowMenuItem(item),
                  );

                if (!groupItems.length) return null;

                return (
                  <div key={group.groupKey} className="navGroup">
                    {!collapsed && (
                      <span className="navGroup__label">{group.label}</span>
                    )}
                    {groupItems.map((item) => {
                      if (item.children?.length) {
                        const visibleChildren = item.children.filter(canShowChildItem);
                        if (!visibleChildren.length) return null;
                        const isOpen = openSubMenuKeys.has(item.key);
                        const hasActiveChild = visibleChildren.some(
                          (c) => activeMenu === c.key,
                        );
                        return (
                          <div key={item.key}>
                            <Tooltip
                              title={collapsed ? item.label : undefined}
                              placement="right"
                              mouseEnterDelay={0.4}
                            >
                              <button
                                className={`navItem${hasActiveChild ? " navItem--active" : ""}`}
                                onClick={() => {
                                  if (collapsed) {
                                    handleNavClick(visibleChildren[0].key);
                                  } else {
                                    toggleSubMenu(item.key);
                                  }
                                }}
                                aria-label={item.label}
                              >
                                <span className="navItem__indicator" aria-hidden="true" />
                                <span className="navItem__icon">{item.icon}</span>
                                {!collapsed && (
                                  <>
                                    <span className="navItem__label">{item.label}</span>
                                    <span className="navItem__chevron">
                                      {isOpen ? <DownOutlined /> : <RightOutlined />}
                                    </span>
                                  </>
                                )}
                              </button>
                            </Tooltip>
                            {isOpen && !collapsed && (
                              <div className="navSubMenu">
                                {visibleChildren.map((child) => (
                                  <button
                                    key={child.key}
                                    className={`navItem navItem--child${activeMenu === child.key ? " navItem--active" : ""}${child.disabled ? " navItem--disabled" : ""}`}
                                    onClick={() => !child.disabled && handleNavClick(child.key)}
                                    aria-label={child.label}
                                    aria-current={activeMenu === child.key ? "page" : undefined}
                                    aria-disabled={child.disabled || undefined}
                                  >
                                    <span className="navItem__indicator" aria-hidden="true" />
                                    <span className="navItem__childDot" aria-hidden="true" />
                                    <span className="navItem__label">{child.label}</span>
                                    {child.disabled && (
                                      <span className="navItem__soon">{"Uskoro"}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <Tooltip
                          key={item.key}
                          title={
                            collapsed
                              ? item.label
                              : item.disabled
                                ? "Uskoro"
                                : undefined
                          }
                          placement="right"
                          mouseEnterDelay={0.4}
                        >
                          <button
                            className={`navItem${activeMenu === item.key ? " navItem--active" : ""}${item.disabled ? " navItem--disabled" : ""}`}
                            onClick={() => !item.disabled && handleNavClick(item.key)}
                            aria-label={item.label}
                            aria-current={
                              activeMenu === item.key ? "page" : undefined
                            }
                            aria-disabled={item.disabled || undefined}
                          >
                            <span
                              className="navItem__indicator"
                              aria-hidden="true"
                            />
                            <span className="navItem__icon">{item.icon}</span>
                            {!collapsed && (
                              <>
                                <span className="navItem__label">
                                  {item.label}
                                </span>
                                {item.disabled && (
                                  <span className="navItem__soon">{"Uskoro"}</span>
                                )}
                                {!item.disabled && item.badge !== undefined && (
                                  <span className="navItem__badge">
                                    {item.badge}
                                  </span>
                                )}
                              </>
                            )}
                            {collapsed && item.badge !== undefined && (
                              <span
                                className="navItem__badgeDot"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              })}
            </Spin>
          </nav>

          {/* Footer */}
          <div className="sidebarFooter">
            <div className="sidebarUser">
              <div className="sidebarUser__avatar" aria-hidden="true">
                {userInitials}
              </div>
              {!collapsed && (
                <div className="sidebarUser__info">
                  <span className="sidebarUser__name">
                    {userData?.firstName} {userData?.lastName}
                  </span>
                  <span className="sidebarUser__email">
                    {userData?.email || ""}
                  </span>
                </div>
              )}
            </div>
            <button
              className="sidebarCollapseBtn"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <RightOutlined /> : <LeftOutlined />}
            </button>
          </div>
        </aside>

        {/* ── Main Pane ── */}
        <div className={`mainPane${collapsed ? " mainPane--collapsed" : ""}`}>
          {/* ── Header ── */}
          <header className="appHeader">
            <div className="appHeader__left">
              {/* Mobile-only logo */}
              <div className="appHeader__mobileLogo">
                <div className="sidebarBrand__mark">
                  <img src={FIcon} alt="F" className="sidebarBrand__markImg" />
                </div>
              </div>
              {/* Desktop breadcrumbs */}
              <div className="appHeader__breadcrumbs">
                <span className="appHeader__crumb-section">
                  {getBreadcrumbs().section}
                </span>
                {getBreadcrumbs().page && (
                  <>
                    <span className="appHeader__crumb-sep">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </span>
                    <span className="appHeader__crumb-page">
                      {getBreadcrumbs().page}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="appHeader__center">
              <div
                className="commandSearch"
                role="search"
                aria-label="Global search"
              >
                <SearchOutlined
                  className="commandSearch__icon"
                  aria-hidden="true"
                />
                <span className="commandSearch__text">
                  {t("Search")}
                  <kbd className="commandSearch__kbd">⌘K</kbd>
                </span>
              </div>
            </div>

            <div className="appHeader__right">
              {/* Alert: expiring documents */}
              {expiringCount > 0 && (
                <button
                  className="headerAlert"
                  onClick={() => {
                    setActiveMenu("0");
                    navigation("/");
                  }}
                  title={t("Expiring documents soon")}
                  aria-label={`${expiringCount} ${t("Expiring documents soon")}`}
                >
                  <ExclamationCircleOutlined className="headerAlert__icon" />
                  <span className="headerAlert__badge">{expiringCount}</span>
                </button>
              )}
              {/* Alert: expiring contracts */}
              {expiringContractsCount > 0 && (
                <button
                  className="headerAlert"
                  onClick={() => navigation("/settings?tab=CompanyContracts")}
                  title={t("Expiring company contracts")}
                  aria-label={`${expiringContractsCount} ${t("Expiring company contracts")}`}
                >
                  <FileProtectOutlined className="headerAlert__icon" />
                  <span className="headerAlert__badge">
                    {expiringContractsCount}
                  </span>
                </button>
              )}

              {/* Notifications */}
              <Popover
                content={
                  <List
                    size="small"
                    dataSource={notifications}
                    className="notificationsList"
                    locale={{ emptyText: t("No notifications") }}
                    renderItem={(item) => (
                      <List.Item>
                        <div>
                          <strong>{item.title}</strong>
                          <div>{item.message}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                }
                title={t("Notifications")}
                trigger="click"
                open={openNotification}
                onOpenChange={(visible) => setOpenNotification(visible)}
              >
                <button
                  className="headerAction"
                  aria-label={t("Notifications")}
                >
                  <NotificationsIcon
                    className="headerAction__notifIcon"
                    aria-hidden="true"
                  />
                </button>
              </Popover>

              {/* Subscription */}
              {hasPermission("subscription:view-header") && (
                <button
                  className={`headerAction${unpaidCount > 0 ? " headerAction--alert" : ""}`}
                  onClick={() => {
                    setActiveMenu("-1");
                    navigation("/subscription");
                  }}
                  title={
                    unpaidCount > 0
                      ? `${unpaidCount} neplaćen račun`
                      : t("My subscription")
                  }
                  aria-label={t("Subscription")}
                >
                  <CreditCardOutlined className="headerAction__icon" />
                  {unpaidCount > 0 && (
                    <span className="headerAction__badge">{unpaidCount}</span>
                  )}
                </button>
              )}

              {/* Voice Assistant — styled as primary AI button */}
              {userData?.clientId && hasPermission("voice-assistant:use") && (
                <div className="headerAiBtn">
                  <VoiceAssistantButton clientId={userData.clientId} />
                </div>
              )}

              {/* Language */}
              <SelectLanguage />

              {/* User Menu */}
              <UserMenu
                user={user}
                canSeeSubscription={hasPermission("subscription:view-sidebar")}
                unpaidCount={unpaidCount}
              />
            </div>
          </header>

          {/* ── Page Content ── */}
          <main className="pageContent">
            <Outlet />
          </main>
        </div>
      </div>

      {/* ── Global Spinner ── */}
      {isLoaderActive && (
        <div className="spinnerContainer">
          <Spin />
        </div>
      )}
    </AuthProvider>
  );
};

export default LayoutContainer;
